import React from 'react';
import moment from 'moment';
import { DatePicker, Form, FormGroup, ValidationMessage } from '~/components/Form';
import { ReminderWarningIcon } from '~/components/Reminders';

interface AddReminderProps {
    performAdd: (description: string, date: string) => void;
    setReminderOpen?: null | ((value: boolean) => void);
    existingReminder?: ReminderType;
    setFormDirty?: (value: boolean) => void;
    disabled?: boolean;
}
export function AddReminder({
    performAdd,
    setReminderOpen = null,
    existingReminder = { description: '', due_at: '' } as ReminderType,
    setFormDirty = () => {},
    disabled = false,
}: AddReminderProps) {
    const maxLength = 500;
    const minDate = moment().toDate();
    const [loading, setLoading] = React.useState(false);
    const [newDescription, setNewDescription] = React.useState(existingReminder.description);
    const [dueDate, setDueDate] = React.useState<null | Date>(null);
    const today = moment().utc(true).hour(0).minute(0).second(0);

    const formattedDate = React.useMemo(() => {
        if (!existingReminder.due_at) return null;
        return moment(existingReminder.due_at.slice(0, -6)).toDate();
    }, [existingReminder.due_at]);

    React.useEffect(() => {
        setDueDate(formattedDate);
    }, [formattedDate]);

    React.useEffect(() => {
        if (setFormDirty) {
            if (
                existingReminder.description !== newDescription ||
                !moment(formattedDate).isSame(dueDate)
            ) {
                setFormDirty(true);
            } else {
                setFormDirty(false);
            }
        }
    }, [existingReminder, dueDate, formattedDate, newDescription, setFormDirty]);

    const descriptionLength = React.useMemo(() => {
        return newDescription ? newDescription.length : 0;
    }, [newDescription]);

    async function addReminder() {
        if (today.isAfter(dueDate)) return false;
        setLoading(true);
        try {
            await performAdd(newDescription, moment(dueDate).format('YYYY-MM-DD 00:00:00+00:00'));
            setLoading(false);
            if (setReminderOpen) {
                setReminderOpen(false);
            }
        } catch {
            setLoading(false);
        }
        return true;
    }

    return (
        <div className="row add-note mb-2">
            <div className="col-sm-12">
                <Form
                    id="add-edit-reminder-form"
                    onSubmit={addReminder}
                    values={{ newDescription, dueDate }}
                >
                    <FormGroup htmlFor="newDescription" className="mb-3">
                        {existingReminder.due_at ? (
                            <label className="fw-bold" htmlFor="newDescription">
                                Action
                            </label>
                        ) : null}
                        <textarea
                            id="newDescription"
                            className="form-control"
                            rows={4}
                            maxLength={500}
                            required
                            aria-label="New Note"
                            onChange={(e) => setNewDescription(e.target.value)}
                            value={newDescription}
                            disabled={disabled}
                        />
                        <ValidationMessage type="required">
                            Description is required.
                        </ValidationMessage>
                    </FormGroup>
                    <div className="duedate">
                        <div className="pull-left">
                            <FormGroup htmlFor="dueDate">
                                <input
                                    type="hidden"
                                    value={dueDate === null ? '' : dueDate.toDateString()}
                                    id="dueDate"
                                    data-requireddate="true"
                                />
                                <span className="fw-bold me-2">Due Date:</span>
                                <DatePicker
                                    className="text-center"
                                    name="duedatepicker"
                                    id="duedatepicker"
                                    value={dueDate}
                                    onChange={(e: null | Date) => setDueDate(e)}
                                    disabled={disabled}
                                    minDate={
                                        formattedDate &&
                                        moment(formattedDate).isSameOrBefore(minDate)
                                            ? formattedDate
                                            : minDate
                                    }
                                />
                                {existingReminder.id && (
                                    <ReminderWarningIcon reminder={existingReminder} />
                                )}
                                <ValidationMessage type="requireddate">
                                    Due Date is required.
                                </ValidationMessage>
                                <ValidationMessage type="external" rule={today.isAfter(dueDate)}>
                                    Due Date must be today or in the future.
                                </ValidationMessage>
                            </FormGroup>
                        </div>
                        {!existingReminder.due_at && (
                            <button
                                className="btn btn-primary pull-right ms-2"
                                type="submit"
                                disabled={loading}
                            >
                                Save {loading && <i className="fa-solid fa-spin fa-spinner" />}
                            </button>
                        )}
                        <span className="pull-right mt-2">
                            {descriptionLength} / {maxLength}
                        </span>
                    </div>
                </Form>
            </div>
        </div>
    );
}
